<script setup lang="ts">
import { computed } from 'vue'

import type { MetricViewProps } from './types'

import { Icon } from '@/shared/ui/icon'
import { PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui/logo'

const props = withDefaults(defineProps<MetricViewProps>(), {
  field: '',
  column: null,
  channel: '',
  onlyLogo: false,
  onlyText: false,
  isAttr: false,
  size: 18,
})

const label = computed(
  () =>
    (props.isAttr ? props.column?.attrHeader : props.column?.header) ||
    props.column?.header ||
    props.field,
)
const platform = computed(() => {
  if (props.column?.hideLogo === true) return ''
  const headerImage = props.column?.headerImage?.trim() ?? ''
  if (headerImage) return headerImage
  // A calculated (Agg.) metric has no owning platform, so it never borrows the section channel.
  if (props.column?.isCalculated) return ''
  return props.column?.numeric === true ? props.channel.trim() : ''
})
const logoSrc = computed(() => resolveStaticPlatformLogo(platform.value))
// The channel fallback still counts as a visual even when it has no known logo asset — it renders
// as text via PlatformLogo's `showText`, rather than disappearing silently.
const showChannelText = computed(
  () => !logoSrc.value && platform.value !== '' && platform.value === props.channel.trim(),
)
const hasVisual = computed(() =>
  Boolean(logoSrc.value || props.column?.icon || showChannelText.value),
)
</script>

<template>
  <span class="ui-metric-view inline-flex min-w-0 items-center gap-2xs">
    <template v-if="!onlyText && hasVisual">
      <PlatformLogo
        v-if="logoSrc || showChannelText"
        :platform="platform"
        :channel="channel || null"
        :alt="onlyLogo ? label : ''"
        :size="size"
        v-bind="logoSrc ? { src: logoSrc } : {}"
        :show-text="showChannelText"
      />
      <Icon v-else-if="column?.icon" :name="column.icon" :size="size" aria-hidden="true" />
    </template>
    <span v-if="!onlyLogo" class="ui-metric-view__text min-w-0 truncate">{{ label }}</span>
  </span>
</template>
